import React, { useEffect, useState, useRef } from 'react';
import { fetchData } from './dataFetching';
import { transformData } from './dataTransformation';
import { useLoadingTracker } from '../../contexts/LoadingTrackerContext';
import { getOriginERL } from '../../config/siteConfig';
import SelectedHoleHighlight from '../../clickTools/SelectedHoleHighlight.js';
import { useHoleDisplayRadii } from '../../store/holeDisplayStore';

const holeColumns = {
  'Hole No': 'Hole_ID',
  'X': 'NAT_East',
  'Y': 'NAT_North',
  'Z': 'NAT_RL',
  'Azimuth': 'Collar_Azimuth',
  'Dip': 'Collar_Dip',
  'Depth': 'Max_Depth',
};

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};

export default function HoleRender({key,name,hole_url,assay_url,visible, onLoadComplete, onLoadFailed}) {
  //console.log(hole_url,assay_url)
  const [combinedData, setCombinedData] = useState({});
  const [filterData, setFilterData] = useState({});
  const [filteredHoleIds] = useState([]);
  const [holeCount, setHoleCount] = useState(0);
  const [assayCount, setAssayCount] = useState(0);
  const { startLoading, stopLoading } = useLoadingTracker();
  const objectName = name || `Holes_${hole_url}`;

  useEffect(() => {
    let cancelled = false;
    
    // Start tracking loading
    startLoading(objectName);

    const loadData = async (attempt = 0) => {
      if (cancelled) return;

      try {
        const data = await fetchData(hole_url, assay_url, holeColumns, assayColumns);
        if (cancelled) return;
        
        const transformed = transformData(data, holeColumns, assayColumns, getOriginERL());
        if (!cancelled) {
          setCombinedData(transformed);
          stopLoading(objectName);
          onLoadComplete?.(objectName);
          if (attempt > 0) {
            console.log(`[HoleRender] Successfully loaded ${hole_url}/${assay_url} after ${attempt + 1} attempt(s)`);
          }
        }
      } catch (error) {
        if (cancelled) return;

        // Check if it's a retryable error
        const errorMessage = error?.message || error?.toString() || String(error) || '';
        const errorString = errorMessage.toLowerCase();
        const errorName = (error?.name || '').toLowerCase();
        const errorType = (error?.type || '').toLowerCase();

        const isHttp2Error = 
          errorString.includes('http2') ||
          errorString.includes('err_http2_protocol_error') ||
          errorString.includes('protocol_error') ||
          errorName.includes('http2') ||
          errorType.includes('http2');

        const isNetworkError = 
          errorString.includes('failed to fetch') ||
          errorString.includes('networkerror') ||
          errorString.includes('network') ||
          errorString.includes('aborted') ||
          errorString.includes('timeout') ||
          errorString.includes('load failed') ||
          errorString.includes('could not load') ||
          errorString.includes('fetch') ||
          errorName.includes('network') ||
          errorName.includes('abort') ||
          errorType === 'networkerror' ||
          errorType === 'abort';

        const isRetryableError = isHttp2Error || isNetworkError;

        // Always retry network/HTTP2 errors indefinitely
        // For other errors, retry up to 100 times as a safety measure
        if (isRetryableError || attempt < 100) {
          const backoffDelay = Math.min(Math.pow(2, Math.min(attempt, 5)) * 1000, 30000);
          const errorInfo = errorMessage || errorName || 'Unknown error';
          console.warn(`[HoleRender] Attempt ${attempt + 1} failed for ${hole_url}/${assay_url}${isRetryableError ? ' (network error)' : ''}, retrying in ${backoffDelay/1000}s...`, errorInfo);
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return loadData(attempt + 1);
        }

        // Only log error if we've truly exhausted retries (shouldn't happen for network errors)
        console.error(`[HoleRender] Failed to load ${hole_url}/${assay_url} after ${attempt + 1} attempts:`, error);
        onLoadFailed?.(objectName);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      stopLoading(objectName);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hole_url, assay_url, objectName, startLoading, stopLoading]);

  useEffect(() => {
    const filteredData = filteredHoleIds.length>0
      ? Object.keys(combinedData).reduce((acc, key) => {
          if (filteredHoleIds.includes(key)) {
            acc[key] = combinedData[key];
          }
          return acc;
        }, {})
      : combinedData;

    setHoleCount(Object.keys(filteredData).length);
    let assayNumber = 0
    Object.keys(filteredData).forEach((hole, j) => {
        const data = filteredData[hole].assays;
        assayNumber += data.length
    })
    setAssayCount(assayNumber)
    
    setFilterData(filteredData)

    //console.log(holeCount,assayCount)

  }, [combinedData, filteredHoleIds]);

  return (
    holeCount > 0 && visible &&
    <group visible={visible} key={key} id={key} name={objectName} userData={{ mdvLayerKey: objectName }}>
      <Holes count={holeCount} holes={filterData}/>
      <SelectedHoleHighlight holes={filterData} />
      {assayCount > 0 && <Assays count={assayCount}  holes={filterData}/>}
    </group>
      
  );
}

function Holes({ count, holes }) {
  const ref = useRef();
  const [instancesData, setInstancesData] = useState([]);
  const { holeBox } = useHoleDisplayRadii();
  //console.log(holes)
  useEffect(() => {
    
    let idata = [];
    Object.keys(holes).forEach((hole, i) => {
      const data = holes[hole];
      idata.push(data.data)
      const matrix = data.holeMatrix;
      ref.current.setMatrixAt(i, matrix);      
    });

    setInstancesData(idata)
    ref.current.instanceMatrix.needsUpdate = true;
  }, [holes]);

  return (
    <instancedMesh ref={ref} args={[null, null, count]} userData={{instancesData:instancesData}}  name='Holes'>
      <boxGeometry key={holeBox} args={[holeBox, holeBox, 1]} />
      <meshStandardMaterial attach="material" color={'#000000'}/>
    </instancedMesh>
  );
}

function Assays({ count,holes }) {
  const ref = useRef();
  const [colors, setColors] = useState(new Float32Array());
  const [instancesData, setInstancesData] = useState([]);
  const { legacyAssay: assayRadius } = useHoleDisplayRadii();

  useEffect(() => {
    let colorsArray = [];
    let idata = [];
    let i = 0
    Object.keys(holes).forEach((hole, j) => {
      const data = holes[hole].assays;
      if (data.length > 0) {
        data.forEach((assay) => {
          const matrix = assay.matrix;
          const color = assay.color;
          const data = assay.data;
          idata.push({
            holeID:data[assayColumns.HoleID],
            from:data[assayColumns.from]  + ' m'  ,
            to: data[assayColumns.to]  + ' m' ,
            grade:data[assayColumns.grade] + ' g/t' 
          })
          ref.current.setMatrixAt(i, matrix);
          colorsArray.push(color.r, color.g, color.b);
          i++;
        });
      }
    });

    setInstancesData(idata)
    setColors(Float32Array.from(colorsArray));
    ref.current.instanceMatrix.needsUpdate = true;
  }, [holes]);

  return (
    <instancedMesh ref={ref} args={[null, null, count]} userData={{instancesData:instancesData}} name='Assays'>
      <cylinderGeometry key={assayRadius} args={[assayRadius, assayRadius, 1, 8]} >
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </cylinderGeometry>
      <meshLambertMaterial vertexColors toneMapped={false} />
    </instancedMesh>
   
  );
}